import React, { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { api, apiError } from "@/lib/apiClient";
import { MapPin, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState("login"); // login | forgot

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    if (mode === "forgot") {
      try {
        await api.post("/auth/forgot-password", { email, origin_url: window.location.origin });
        toast.success("Si ce compte existe, un lien de réinitialisation a été envoyé.");
        setMode("login");
      } catch (err) {
        setError(apiError(err));
      } finally {
        setLoading(false);
      }
      return;
    }
    const res = await login(email, password);
    setLoading(false);
    if (res.ok) navigate("/");
    else setError(res.error);
  };

  return (
    <div className="flex min-h-screen">
      <div className="hidden w-1/2 flex-col justify-between bg-[#0A0A0A] p-12 lg:flex">
        <div className="flex items-center gap-2">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-600">
            <MapPin className="h-6 w-6 text-white" />
          </div>
          <span className="font-heading text-3xl font-black tracking-tighter text-white">CVT</span>
        </div>
        <div>
          <h1 className="font-heading text-4xl font-black leading-tight text-white">
            Gestion des<br />visites techniques
          </h1>
          <p className="mt-4 max-w-md text-slate-400">
            La plateforme qui relie CVT, partenaires, planification et techniciens terrain. Planifiez, affectez et suivez chaque visite en temps réel.
          </p>
        </div>
        <div className="text-xs text-slate-600">© 2026 CVT · Outil professionnel</div>
      </div>

      <div className="flex w-full items-center justify-center bg-white px-6 lg:w-1/2">
        <div className="w-full max-w-sm">
          <div className="mb-8 lg:hidden">
            <span className="font-heading text-3xl font-black tracking-tighter">CVT</span>
          </div>
          <h2 className="font-heading text-2xl font-extrabold tracking-tight text-slate-900">
            {mode === "login" ? "Connexion" : "Mot de passe oublié"}
          </h2>
          <p className="mt-1 text-sm text-slate-500">
            {mode === "login" ? "Accédez à votre espace CVT" : "Recevez un lien de réinitialisation"}
          </p>

          <form onSubmit={submit} className="mt-8 space-y-4">
            <div>
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-slate-500">Email</label>
              <input
                data-testid="login-email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full rounded-lg border border-slate-200 px-3 py-2.5 text-sm outline-none transition-colors duration-200 focus:border-slate-900"
                placeholder="vous@cvt.fr"
              />
            </div>
            {mode === "login" && (
              <div>
                <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-slate-500">Mot de passe</label>
                <input
                  data-testid="login-password"
                  type="password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full rounded-lg border border-slate-200 px-3 py-2.5 text-sm outline-none transition-colors duration-200 focus:border-slate-900"
                  placeholder="••••••••"
                />
              </div>
            )}
            {error && <div data-testid="login-error" className="rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-700">{error}</div>}
            <button
              data-testid="login-submit"
              type="submit"
              disabled={loading}
              className="flex w-full items-center justify-center gap-2 rounded-lg bg-[#0A0A0A] py-2.5 text-sm font-semibold text-white transition-colors duration-200 hover:bg-slate-800 disabled:opacity-60"
            >
              {loading && <Loader2 className="h-4 w-4 animate-spin" />}
              {mode === "login" ? "Se connecter" : "Envoyer le lien"}
            </button>
            <button
              type="button"
              onClick={() => { setMode(mode === "login" ? "forgot" : "login"); setError(""); }}
              className="w-full text-center text-xs text-slate-500 hover:text-slate-900"
            >
              {mode === "login" ? "Mot de passe oublié ?" : "← Retour à la connexion"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
